/**
 * How a browser, a Worker and a Durable Object independently compute the name
 * of the same account object — and nothing else.
 *
 * ## Why this is in `wire/`
 *
 * Three parties need it and none of them can ask the others. The app frame
 * derives it from the token it is about to present, `/mcp` derives it from the
 * OAuth token, and the Durable Object recomputes it to check that the socket
 * that landed on it actually belongs there. `bridge/` may not import `relay/`,
 * so the shared half has to live somewhere both can reach.
 *
 * **It is an addressing format, not a secret operation.** Every value here is
 * public: a hash of a public principal, already visible in every dial URL. That
 * is the line this file holds — nothing in it can be used to gain access to
 * anything, which is what makes it safe to ship into every app bundle.
 *
 * ## What used to be here and is gone
 *
 * This file was `wire/install.ts`, the FORMAT of a pairing install id — a
 * bearer credential a browser persisted and presented to rejoin an account with
 * no human present. That mechanism was replaced on 2026-09-14: every frame now
 * proves the user directly, with a token the Giraffe host signed. There is no
 * stored credential, so there is no id to format, parse, or namespace, and the
 * file is named after what it still does.
 */

/** 128 bits of hex. Long enough that a collision is not a threat model. */
const PRINCIPAL_REF_HEX = 32;

/**
 * Whether a string is shaped like a principal ref, checked BEFORE it is used to
 * name a Durable Object.
 *
 * Not hygiene: `idFromName` will happily mint an object for any string a caller
 * invents, so an unchecked `?ref=` on a public endpoint is an invitation to
 * create objects by the million. The shape is the whole check — a well-formed
 * ref naming somebody else's account is refused by the object itself, which is
 * what `relay/admission.ts` is for.
 */
export function isPrincipalRef(value: string): boolean {
  return value.length === PRINCIPAL_REF_HEX && /^[0-9a-f]+$/.test(value);
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * The account object's address, derived from the principal.
 *
 * Hashed rather than used raw so the account key — a Giraffe user id since
 * 2026-09-14 — never appears in a Durable Object name, a log line or a URL.
 *
 * ⚠️ The browser computes this and puts it in the dial URL, so a liar can name
 * any object they like. What makes it binding is that the Durable Object
 * recomputes it from the identity it just VERIFIED and refuses the socket if
 * the two disagree. The ref is a routing hint; `relay/admission.ts` is the line.
 */
export async function principalRefFor(tenant: string, principal: string): Promise<string> {
  return (await sha256Hex(`${tenant}:${principal}`)).slice(0, PRINCIPAL_REF_HEX);
}

/** The account object's name. `docs/identity.md`'s `DO id = hash(tenant, account)`, literally. */
export function accountDoName(tenant: string, principalRef: string): string {
  return `${tenant}:account:${principalRef}`;
}

/**
 * The `user_id` claim of a token, read WITHOUT VERIFYING ANYTHING.
 *
 * ## Read the next paragraph before using this for anything
 *
 * **This is not identity and must never be treated as it.** It is a base64url
 * decode and a property read on attacker-controlled text: anyone can mint a
 * token claiming to be anyone. Its ONE legitimate use is computing a dial
 * address, because the relay cannot verify before it routes — the token arrives
 * in `hello`, after the socket is attached, and putting it in the dial URL is
 * what #17 forbids.
 *
 * So the browser says where it thinks it belongs and **the Durable Object
 * proves it**: `relay/admission.ts` verifies the token against the pinned host
 * key and refuses the socket if the verified identity does not hash to the
 * object it landed on. A liar reaches an object that immediately rejects them.
 *
 * It lives here rather than in `identity/` for the reason `principalRefFor`
 * does: `bridge/` needs it and may import neither `identity/` nor `relay/`.
 * That is also the honest placement — a decode with no trust attached is a wire
 * format operation, and putting it beside the real verifier is what would
 * invite somebody to mistake one for the other.
 *
 * Returns null rather than a guess for anything it cannot read, because a
 * subject nobody can name has no address to dial.
 */
export function unverifiedSubject(token: string): string | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  const segment = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = segment + "=".repeat((4 - (segment.length % 4)) % 4);
  let json: string;
  try {
    json = atob(padded);
  } catch {
    return null;
  }
  let payload: unknown;
  try {
    payload = JSON.parse(json);
  } catch {
    return null;
  }
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) return null;
  const subject = (payload as Record<string, unknown>).user_id;
  // A number in the live payload, stringified here for the same reason
  // `GiraffeClaims.userId` is: `principalRefFor` takes a string.
  if (typeof subject === "number") return String(subject);
  return typeof subject === "string" && subject.length > 0 ? subject : null;
}

/**
 * The relay's socket endpoint, derived from its HTTP one.
 *
 * Derived rather than configured, because the relay's address used to be
 * written down in three places — the broker's `RELAY_WS_URL` and each app
 * Worker's `RELAY_HTTP_URL` — and #22 deleted the broker that held one of them.
 * Two copies of an address is two chances for a deploy to point half the fleet
 * at the wrong relay, and the failure presents as a socket that never opens
 * rather than as a configuration error.
 *
 * ⚠️ The scheme is mapped, never assumed: a local relay on `http://localhost`
 * cannot be dialled over `wss:`, and hard-coding the secure scheme would break
 * `yarn dev` alone while leaving every deployed origin working.
 */
export function relaySocketUrl(httpUrl: string): string {
  const url = new URL(httpUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  // The configured value is an ORIGIN everywhere it is set; a stray path would
  // otherwise produce `/mcp/ws`, which routes nowhere.
  url.pathname = "/ws";
  url.search = "";
  url.hash = "";
  return url.toString();
}
